import React, { useState, useEffect, useMemo, ChangeEvent } from "react"
import { useTranslation, Trans } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { User } from "@terveystalo/web-auth-onewelcome"

import { Button, Title, Lead, TextField } from "@terveystalo/design-system-react-components"
import { Autocomplete } from "./components/Autocomplete"
import authClient from "./UserManager"
import { getBackend, ToimipaikkaData as LocationData } from "./client"
import { config } from "./config"
import { useFormPersistence } from "./hooks/useFormPersistence"

const client = getBackend()

interface MuistutusFormProps {
  user: User | null
  isLoggedIn: boolean
}

interface MuistutusFormState {
  onBehalfOf: boolean
  patientName: string
  patientSsn: string
  patientPhone: string
  patientEmail: string
  patientAddress: string
  purchaseServiceCustomer: boolean
  representativeName: string
  representativeSsn: string
  representativePhone: string
  representativeEmail: string
  representativeAddress: string
  toimipaikka?: string
  muistutus: string
  responseMethod: string
}

const muistutusFormInitialState: MuistutusFormState = {
  onBehalfOf: false,
  patientName: "",
  patientSsn: "",
  patientPhone: "",
  patientEmail: "",
  patientAddress: "",
  purchaseServiceCustomer: false,
  representativeName: "",
  representativeSsn: "",
  representativePhone: "",
  representativeEmail: "",
  representativeAddress: "",
  toimipaikka: undefined,
  muistutus: "",
  responseMethod: "",
}

function MuistutusForm({ user: _user, isLoggedIn }: MuistutusFormProps) {
  const { t, i18n } = useTranslation()
  const routeLocation = useLocation()
  const navigate = useNavigate()

  // Detect print mode from URL query parameter
  const searchParams = new URLSearchParams(routeLocation.search)
  const isPrintMode = searchParams.get("mode") === "print"

  const { formState, setFormState, saveFormState, isRestoring } = useFormPersistence({
    formKey: "muistutusForm",
    initialState: muistutusFormInitialState,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState("")
  const [locationList, setLocationList] = useState<LocationData[]>([])
  const [files, setFiles] = useState<File[]>([])
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  // Update page title when language changes
  React.useEffect(() => {
    document.title = t("pageTitleMuistutus")
  }, [i18n.language, t])

  // Disable scrolling when submit message modal is open
  React.useEffect(() => {
    if (submitMessage) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [submitMessage])

  // Fetch location list on component mount
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const data = await client.getToimipaikka()
        if (data.data && data.data.length > 0) {
          setLocationList(data.data)
        }
      } catch (error) {
        console.error("Failed to fetch locations:", error)
      }
    }
    fetchLocations()
  }, [])

  // Create location options from fetched list
  const locationOptions = useMemo(() => {
    return [
      { label: t("muistutusForm.toimipaikkaSelect"), value: "" },
      ...locationList.map((loc) => {
        const translationKey = `toimipaikkaNames.${loc.name}` as const
        const translatedName = t(translationKey, { defaultValue: loc.name })
        return {
          label: translatedName,
          value: loc.id,
        }
      }),
    ]
  }, [locationList, t])

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(Array.from(e.target.files))
    }
  }

  const handleOnBehalfOfChange = (value: boolean) => {
    setFormState((prevState) => ({
      ...prevState,
      onBehalfOf: value,
      // Reset response method if switching to on behalf and had "app" selected
      responseMethod: value && prevState.responseMethod === "app" ? "" : prevState.responseMethod,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitMessage("")

    // Validation
    if (!formState.patientName.trim()) {
      setSubmitMessage(t("error.patientNameRequired"))
      setIsSubmitting(false)
      return
    }
    if ((!isLoggedIn || formState.onBehalfOf) && !formState.patientSsn.trim()) {
      setSubmitMessage(t("error.patientSsnRequired"))
      setIsSubmitting(false)
      return
    }
    if (!formState.patientPhone.trim()) {
      setSubmitMessage(t("error.patientPhoneRequired"))
      setIsSubmitting(false)
      return
    }

    // Email and address required when NOT on behalf
    if (!formState.onBehalfOf) {
      if (!formState.patientEmail.trim()) {
        setSubmitMessage(t("error.patientEmailRequired"))
        setIsSubmitting(false)
        return
      }
      if (!formState.patientAddress.trim()) {
        setSubmitMessage(t("error.patientAddressRequired"))
        setIsSubmitting(false)
        return
      }
    }

    // Representative fields required when on behalf
    if (formState.onBehalfOf) {
      if (!formState.representativeName.trim()) {
        setSubmitMessage(t("error.representativeNameRequired"))
        setIsSubmitting(false)
        return
      }
      if (!formState.representativeSsn.trim()) {
        setSubmitMessage(t("error.representativeSsnRequired"))
        setIsSubmitting(false)
        return
      }
      if (!formState.representativePhone.trim()) {
        setSubmitMessage(t("error.representativePhoneRequired"))
        setIsSubmitting(false)
        return
      }
      if (!formState.representativeEmail.trim()) {
        setSubmitMessage(t("error.representativeEmailRequired"))
        setIsSubmitting(false)
        return
      }
      if (!formState.representativeAddress.trim()) {
        setSubmitMessage(t("error.representativeAddressRequired"))
        setIsSubmitting(false)
        return
      }
    }

    if (!formState.toimipaikka) {
      setSubmitMessage(t("error.toimipaikkaRequired"))
      setIsSubmitting(false)
      return
    }

    if (!formState.responseMethod) {
      setSubmitMessage(t("error.responseMethodRequired"))
      setIsSubmitting(false)
      return
    }

    // File required when on behalf
    if (formState.onBehalfOf && files.length === 0) {
      setSubmitMessage(t("error.fileRequiredOnBehalf"))
      setIsSubmitting(false)
      return
    }

    // Either muistutus text or file required
    if (!formState.muistutus.trim() && files.length === 0) {
      setSubmitMessage(t("error.muistutusOrFileRequired"))
      setIsSubmitting(false)
      return
    }

    try {
      const formData = new FormData()

      formData.append("onBehalfOf", String(formState.onBehalfOf))
      formData.append("patientName", formState.patientName)
      if (!isLoggedIn || formState.onBehalfOf) {
        formData.append("patientSsn", formState.patientSsn)
      }
      formData.append("patientPhone", formState.patientPhone)

      if (!formState.onBehalfOf) {
        formData.append("patientEmail", formState.patientEmail)
        formData.append("patientAddress", formState.patientAddress)
        formData.append("purchaseServiceCustomer", String(formState.purchaseServiceCustomer))
      }

      if (formState.onBehalfOf) {
        formData.append("representativeName", formState.representativeName)
        formData.append("representativeSsn", formState.representativeSsn)
        formData.append("representativePhone", formState.representativePhone)
        formData.append("representativeEmail", formState.representativeEmail)
        formData.append("representativeAddress", formState.representativeAddress)
      }

      formData.append("toimipaikka", formState.toimipaikka!)
      if (formState.muistutus.trim()) {
        formData.append("muistutus", formState.muistutus)
      }
      formData.append("responseMethod", formState.responseMethod)

      files.forEach((file) => {
        formData.append("files", file)
      })

      const backendUrl = config.backendHost
      const user = await authClient.getUser()
      const response = await fetch(`${backendUrl}/api/muistutus`, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: `Bearer ${user?.access_token}`,
          ...(user?.id_token ? { "X-Id-Token": user.id_token } : {}),
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      setSubmitMessage(t("success.muistutusSubmitted"))
      setFormState(muistutusFormInitialState)
      setFiles([])
    } catch (error) {
      console.error(error)
      setSubmitMessage(
        `${t("error.generic")}: ${error instanceof Error ? error.message : t("error.submitFailed")}`,
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const login = async () => {
    console.log("[MuistutusForm] Authenticate clicked - Current location:", routeLocation.pathname)
    await saveFormState()
    sessionStorage.setItem("returnUrl", routeLocation.pathname)

    try {
      await authClient.signinRedirect()
    } catch (error) {
      console.error("[MuistutusForm] Error during signinRedirect:", error)
    }
  }

  // Show loading state while restoring form data
  if (isRestoring) {
    return (
      <div className="min-h-screen bg-coal04 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lake mx-auto mb-4"></div>
          <p className="text-coal65">{t("form.loading")}</p>
        </div>
      </div>
    )
  }

  // Show login prompt if not authenticated and not in print mode
  if (!isLoggedIn && !isPrintMode) {
    return (
      <div className="min-h-screen bg-coal04">
        <div className="w-full m:max-w-7xl m:mx-auto">
          <header className="bg-woolhead shadow-sm border-b border-coal15">
            <div className="px-4 s:px-6 m:px-8 py-2">
              <Title>{t("muistutusForm.title")}</Title>
            </div>
          </header>
          <main className="s:px-6 m:px-8">
            <div
              className="bg-woolhead rounded-lg shadow p-4 mt-4"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              {/* Authenticated option */}
              <div className="mb-4 text-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="mx-auto mb-2 h-5 w-5 text-coal65"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                  />
                </svg>
                <p className="mb-2 font-bold">{t("muistutusForm.authenticatedExplanation")}</p>
                <p className="mb-3 text-sm">{t("muistutusForm.notAuthenticatedMessage")}</p>
                <Button onClick={login}>{t("login")}</Button>
              </div>

              {/* Divider */}
              <div className="my-4 border-t border-gray-300"></div>

              {/* Print mode option */}
              <div className="text-center">
                <p className="mb-3 text-sm">{t("muistutusForm.printModeExplanation")}</p>
                <Button
                  onClick={() => navigate(routeLocation.pathname + "?mode=print")}
                  variant="secondary"
                >
                  {t("muistutusForm.usePrintableForm")}
                </Button>
              </div>
            </div>
          </main>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      <div className="min-h-screen bg-coal04">
        <div ref={wrapperRef} className="w-full m:max-w-7xl m:mx-auto">
          <header className="bg-woolhead shadow-sm border-b border-coal15 no-print">
            <div className="px-4 s:px-6 m:px-8 py-2">
              <Title>{t("muistutusForm.title")}</Title>
              <Lead>
                <Trans
                  i18nKey="muistutusForm.lead"
                  components={{
                    red: <span style={{ color: "#CE0F69" }} />,
                  }}
                />
              </Lead>
            </div>
          </header>
          <main className={`s:px-6 m:px-8${isPrintMode ? "" : " no-print"}`}>
            <div
              className="bg-woolhead rounded-lg shadow p-2"
              style={{ backgroundColor: "#f8f9fa" }}
            >
              {/* Print mode banner */}
              {isPrintMode && (
                <div className="mb-4 p-3 bg-yellow-50 rounded border border-yellow-400 no-print">
                  <p className="text-sm font-bold text-yellow-900">
                    {t("muistutusForm.printModeBanner")}
                  </p>
                  <p className="text-sm text-yellow-800 mt-1">
                    {t("muistutusForm.printInstructions")}
                  </p>
                </div>
              )}

              {/* On Behalf Of */}
              <div className="space-y-1 mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  {t("muistutusForm.onBehalfOfLabel")} <span style={{ color: "#CE0F69" }}>*</span>
                </label>
                <div className="flex gap-3">
                  <label className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                    <input
                      type="radio"
                      name="onBehalfOf"
                      value="true"
                      checked={formState.onBehalfOf === true}
                      onChange={() => handleOnBehalfOfChange(true)}
                      className="w-3.5 h-3.5"
                    />
                    {t("muistutusForm.onBehalfOfYes")}
                  </label>
                  <label className="flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                    <input
                      type="radio"
                      name="onBehalfOf"
                      value="false"
                      checked={formState.onBehalfOf === false}
                      onChange={() => handleOnBehalfOfChange(false)}
                      className="w-3.5 h-3.5"
                    />
                    {t("muistutusForm.onBehalfOfNo")}
                  </label>
                </div>
              </div>

              {/* Conditional info panel when on behalf */}
              {formState.onBehalfOf && (
                <div className="mb-2 px-2.5 py-1 bg-yellow-50 rounded border border-yellow-400 text-sm">
                  <p className="font-bold text-yellow-900 mb-0.5">
                    {t("muistutusForm.onBehalfOfInfoTitle")}
                  </p>
                  <p className="text-yellow-800 mb-1">{t("muistutusForm.onBehalfOfInfoText")}</p>
                  <p className="text-yellow-800">
                    {t("muistutusForm.readMore")}{" "}
                    <a
                      href={t("muistutusForm.onBehalfOfInfoUrl")}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      {t("muistutusForm.onBehalfOfInfoLink")}
                    </a>
                  </p>
                </div>
              )}

              {/* Patient Information Section */}
              <Title as="h2" size="l" className="mb-2">
                {t("muistutusForm.patientInfoTitle")}
              </Title>

              <div className="space-y-2 mb-4">
                <TextField
                  id="patientName"
                  name="patientName"
                  label={t("muistutusForm.patientNameLabel")}
                  placeholder={t("muistutusForm.patientNamePlaceholder")}
                  value={formState.patientName}
                  onChange={handleInputChange}
                  required
                />

                {(!isLoggedIn || formState.onBehalfOf) && (
                  <TextField
                    id="patientSsn"
                    name="patientSsn"
                    label={t("muistutusForm.patientSsnLabel")}
                    placeholder={t("muistutusForm.patientSsnPlaceholder")}
                    value={formState.patientSsn}
                    onChange={handleInputChange}
                    required
                  />
                )}

                <TextField
                  id="patientPhone"
                  name="patientPhone"
                  type="tel"
                  label={t("muistutusForm.patientPhoneLabel")}
                  placeholder={t("muistutusForm.patientPhonePlaceholder")}
                  value={formState.patientPhone}
                  onChange={handleInputChange}
                  required
                />

                {/* Email - only when NOT on behalf */}
                {!formState.onBehalfOf && (
                  <TextField
                    id="patientEmail"
                    name="patientEmail"
                    type="email"
                    label={t("muistutusForm.patientEmailLabel")}
                    placeholder={t("muistutusForm.patientEmailPlaceholder")}
                    value={formState.patientEmail}
                    onChange={handleInputChange}
                    required
                  />
                )}

                {/* Address - only when NOT on behalf */}
                {!formState.onBehalfOf && (
                  <TextField
                    id="patientAddress"
                    name="patientAddress"
                    label={t("muistutusForm.patientAddressLabel")}
                    placeholder={t("muistutusForm.patientAddressPlaceholder")}
                    value={formState.patientAddress}
                    onChange={handleInputChange}
                    required
                  />
                )}

                {/* Purchase service customer checkbox - only when NOT on behalf */}
                {!formState.onBehalfOf && (
                  <div className="space-y-2 mb-4">
                    <label className="flex items-start">
                      <input
                        type="checkbox"
                        name="purchaseServiceCustomer"
                        checked={formState.purchaseServiceCustomer}
                        onChange={(e) =>
                          setFormState((prev) => ({
                            ...prev,
                            purchaseServiceCustomer: e.target.checked,
                          }))
                        }
                        className="mr-2 mt-1 flex-shrink-0"
                      />
                      <span className="text-sm">
                        {t("muistutusForm.purchaseServiceCustomerLabel")}
                      </span>
                    </label>
                  </div>
                )}
              </div>

              {/* Representative Information Section - only when on behalf */}
              {formState.onBehalfOf && (
                <>
                  <Title as="h2" size="l" className="mb-2">
                    {t("muistutusForm.representativeInfoTitle")}
                  </Title>

                  <div className="space-y-2 mb-4">
                    <TextField
                      id="representativeName"
                      name="representativeName"
                      label={t("muistutusForm.representativeNameLabel")}
                      placeholder={t("muistutusForm.representativeNamePlaceholder")}
                      value={formState.representativeName}
                      onChange={handleInputChange}
                      required
                    />

                    <TextField
                      id="representativeSsn"
                      name="representativeSsn"
                      label={t("muistutusForm.representativeSsnLabel")}
                      placeholder={t("muistutusForm.representativeSsnPlaceholder")}
                      value={formState.representativeSsn}
                      onChange={handleInputChange}
                      required
                    />

                    <TextField
                      id="representativePhone"
                      name="representativePhone"
                      type="tel"
                      label={t("muistutusForm.representativePhoneLabel")}
                      placeholder={t("muistutusForm.representativePhonePlaceholder")}
                      value={formState.representativePhone}
                      onChange={handleInputChange}
                      required
                    />

                    <TextField
                      id="representativeEmail"
                      name="representativeEmail"
                      type="email"
                      label={t("muistutusForm.representativeEmailLabel")}
                      placeholder={t("muistutusForm.representativeEmailPlaceholder")}
                      value={formState.representativeEmail}
                      onChange={handleInputChange}
                      required
                    />

                    <TextField
                      id="representativeAddress"
                      name="representativeAddress"
                      label={t("muistutusForm.representativeAddressLabel")}
                      placeholder={t("muistutusForm.representativeAddressPlaceholder")}
                      value={formState.representativeAddress}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </>
              )}

              {/* Reminder Details Section */}
              <Title as="h2" size="l" className="mb-2">
                {t("muistutusForm.reminderDetailsTitle")}
              </Title>

              <div className="space-y-2 mb-4">
                <Autocomplete
                  id="toimipaikka"
                  name="toimipaikka"
                  label={t("muistutusForm.toimipaikkaLabel")}
                  placeholder={t("muistutusForm.toimipaikkaPlaceholder")}
                  options={locationOptions}
                  value={formState.toimipaikka || ""}
                  onChange={(value) => setFormState({ ...formState, toimipaikka: value })}
                  required
                />

                <TextField
                  id="muistutus"
                  name="muistutus"
                  label={`${t("muistutusForm.muistutusLabel")}${formState.onBehalfOf || files.length > 0 ? "" : " *"}`}
                  placeholder={t("muistutusForm.muistutusPlaceholder")}
                  as="textarea"
                  value={formState.muistutus}
                  onChange={handleInputChange}
                  rows={5}
                />
              </div>

              {/* Response Method */}
              <div className="space-y-2 mb-4">
                <label className="block text-sm font-medium text-gray-700">
                  {t("muistutusForm.responseMethodTitle")}{" "}
                  <span style={{ color: "#CE0F69" }}>*</span>
                </label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="responseMethod"
                      value="encrypted_email"
                      checked={formState.responseMethod === "encrypted_email"}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    {t("muistutusForm.responseMethodEncryptedEmail")}
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="responseMethod"
                      value="post"
                      checked={formState.responseMethod === "post"}
                      onChange={handleInputChange}
                      className="mr-2"
                    />
                    {t("muistutusForm.responseMethodPost")}
                  </label>
                  <label
                    className={`flex items-center ${formState.onBehalfOf ? "opacity-50" : ""}`}
                  >
                    <input
                      type="radio"
                      name="responseMethod"
                      value="app"
                      checked={formState.responseMethod === "app"}
                      onChange={handleInputChange}
                      disabled={formState.onBehalfOf}
                      className="mr-2"
                    />
                    <span>
                      {t("muistutusForm.responseMethodApp")}
                      {formState.onBehalfOf && (
                        <span className="text-sm text-gray-500 ml-1">
                          ({t("muistutusForm.responseMethodAppDisabled")})
                        </span>
                      )}
                    </span>
                  </label>
                </div>
              </div>

              {/* File Upload - hidden in print mode */}
              {!isPrintMode && (
                <div className="space-y-2 mb-4">
                  <label
                    htmlFor="file-upload-muistutus"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("form.attachments")}
                    {formState.onBehalfOf && <span style={{ color: "#CE0F69" }}> *</span>}
                    {!formState.onBehalfOf && !formState.muistutus.trim() && (
                      <span style={{ color: "#CE0F69" }}> *</span>
                    )}
                  </label>
                  <input
                    id="file-upload-muistutus"
                    name="files"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    accept="image/*,.pdf,.doc,.docx,.txt"
                  />
                  {files.length > 0 && (
                    <p className="text-sm text-gray-600 mt-1">
                      {t("form.filesSelected", { count: files.length })}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">{t("form.fileUploadHint")}</p>
                </div>
              )}

              <p className="text-coal65 mb-6">
                {t("form.privacyNotice")}
                <a href={t("form.privacyStatementUrl")} className="link">
                  {t("form.privacyStatement")}
                </a>
                {t("form.privacyCustomers")}
              </p>

              {/* Show print button in print mode, submit button otherwise */}
              {isPrintMode ? (
                <Button type="button" onClick={() => window.print()} className="no-print">
                  {t("muistutusForm.printButton")}
                </Button>
              ) : (
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t("form.submitting") : t("submit")}
                </Button>
              )}
            </div>
          </main>
        </div>

        {/* Success/Error Modal */}
        {submitMessage && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <div
              style={{
                backgroundColor: "#fff",
                padding: "32px",
                borderRadius: "8px",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                maxWidth: "500px",
                textAlign: "center",
              }}
            >
              <p style={{ marginBottom: "24px", fontSize: "1.125rem" }}>{submitMessage}</p>
              <Button
                type="button"
                onClick={() => (window.location.href = "https://www.terveystalo.com")}
              >
                {t("close")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </form>
  )
}

export default MuistutusForm
